import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { lenisInstance, LenisScroll } from '../utils/lenis-scroll'

const PlataArsinsPage = {
  breakpoints: {
    isDesktop: `(min-width: 768px)`,
    isMobile: `(max-width: 767px)`,
  },

  debug: false,

  init(): void {
    if (!document.querySelector('.plata-arsins-page')) {
      return
    }

    LenisScroll.init()
    gsap.registerPlugin(ScrollTrigger)
    gsap.config({ force3D: false })

    const start = () => {
      if (this.debug) {
        gsap.set('.pa-intro', { display: 'none' })
        return
      }

      document.querySelector('.plata-arsins-page').classList.add('plata-arsins-page--loaded')

      this.section.intro()

      this.section.hero()
      this.section.info()
      this.section.cards()
      this.section.footer()
    }

    // if (document.readyState === 'loading') {
    //   document.addEventListener(
    //     'DOMContentLoaded',
    //     () => {
    //       start()
    //     },
    //     { once: true },
    //   )
    // } else {
    //   start()
    // }

    window.addEventListener(
      'load',
      () => {
        start()
        ScrollTrigger.refresh()
      },
      { once: true },
    )
  },

  helper: {
    chocoParallax(selector, yDesktop, yMobile, rotation): void {
      gsap.matchMedia().add(PlataArsinsPage.breakpoints, (context) => {
        const { isDesktop } = context.conditions

        ScrollTrigger.create({
          animation: gsap.to(`${selector} > div`, { ease: 'none', rotation, y: isDesktop ? yDesktop : yMobile }),
          // end: 'clamp(100% 0%)',
          end: '100% 0%',
          scrub: true,
          // start: 'clamp(0% 100%)',
          start: '0% 100%',
          trigger: selector,
        })
      })
    },

    cloudParallax(selector, xDesktop, xMobile): void {
      gsap.matchMedia().add(PlataArsinsPage.breakpoints, (context) => {
        const { isDesktop } = context.conditions

        ScrollTrigger.create({
          animation: gsap.to(`${selector} > div`, { ease: 'none', x: isDesktop ? xDesktop : xMobile }),
          // end: 'clamp(100% 0%)',
          end: '100% 0%',
          scrub: true,
          // start: 'clamp(0% 100%)',
          start: '0% 100%',
          trigger: selector,
        })
      })
    },
  },

  section: {
    intro(): void {
      gsap.matchMedia().add(PlataArsinsPage.breakpoints, (context) => {
        const { isDesktop } = context.conditions

        lenisInstance?.stop()

        // Cover fade out + intro clouds slide in

        gsap
          //
          .timeline({ defaults: { duration: 0.7, ease: 'power3.out' } })
          .set('.pa-intro__cover', { opacity: 1 })
          .set('.pa-hero .pa-cloud--1', { x: '-90rem' })
          .set('.pa-hero .pa-cloud--2', { x: '90rem' })
          .set('.pa-intro .pa-cloud--1', { x: '-50rem' })
          .set('.pa-intro .pa-cloud--2', { x: '50rem' })
          .set('.pa-intro .pa-cloud--3', { x: '-50rem' })
          .set('.pa-intro .pa-cloud--4', { x: '-20rem' })
          //
          .to('.pa-intro__cover', { duration: 2, opacity: 0 }, 0.21)
          .to('.pa-intro .pa-cloud', { x: '0rem' }, '<')
          .to('.pa-hero .pa-cloud--1, .pa-hero .pa-cloud--2', { duration: 7, x: '0rem' }, '<')

        // Intro clouds slide out

        const cloudProps = { duration: 0.5, opacity: 0, scale: 1 }
        const cloudsExitDelay = 0.6

        gsap
          //
          .timeline({ defaults: { duration: 1, ease: 'power3.in' } })
          .to('.pa-intro .pa-cloud--1', { x: '-1200rem' }, cloudsExitDelay + 0.2)
          .to('.pa-intro .pa-cloud--1', cloudProps, '<0.5')
          .to('.pa-intro .pa-cloud--2', { x: '1400rem' }, cloudsExitDelay + 0.1)
          .to('.pa-intro .pa-cloud--2', cloudProps, '<0.5')
          .to('.pa-intro .pa-cloud--3', { duration: 1.2, x: '-900rem' }, cloudsExitDelay)
          .to('.pa-intro .pa-cloud--3', cloudProps, '<0.5')
          .to('.pa-intro .pa-cloud--4', { duration: 1.3, x: '1600rem' }, cloudsExitDelay)
          .to('.pa-intro .pa-cloud--4', cloudProps, '<0.5')

        gsap.delayedCall(1.3, () => {
          lenisInstance?.start()
        })

        // Hero animation

        gsap
          //
          .timeline({ defaults: { duration: 1.5, ease: 'power3.out' } })
          .set('.pa-header__logo', { opacity: 0, scale: 0 })
          .set('.pa-hero__title', { scale: 0.9 })
          .set('.pa-hero__glow > div:last-child', { opacity: 0 })
          .set('.pa-hero .pa-choco picture', { y: isDesktop ? '70rem' : '35rem' })
          .set('.pa-hero .pa-choco--1 picture, .pa-hero .pa-choco--3 picture, .pa-hero .pa-choco--4 picture', { y: isDesktop ? '120rem' : '70rem' })
          .set('.pa-hero .pa-cloud--3', { x: '-70rem' })
          .set('.pa-hero .pa-cloud--4', { x: '70rem' })
          .set('.pa-info .pa-cloud--1', { x: '110rem' })
          //
          .to('.pa-header__logo', { opacity: 1, scale: 1 }, 1)
          .to('.pa-hero__title', { duration: 5, scale: 1 }, '<')
          .to('.pa-hero__glow > div:last-child', { duration: 5, opacity: 1 }, '<')
          .to('.pa-hero .pa-cloud--3, .pa-hero .pa-cloud--4, .pa-info .pa-cloud--1', { duration: 3, x: '0rem' }, '<')
          .to('.pa-hero .pa-choco picture', { duration: 2.5, y: '0rem' }, '<')
      })
    },

    hero(): void {
      PlataArsinsPage.helper.chocoParallax('.pa-hero .pa-choco--1', '-70rem', '-20rem', -12)
      PlataArsinsPage.helper.chocoParallax('.pa-hero .pa-choco--2', '-30rem', '-30rem', 17)
      PlataArsinsPage.helper.chocoParallax('.pa-hero .pa-choco--3', '-70rem', '-70rem', -33)
      PlataArsinsPage.helper.chocoParallax('.pa-hero .pa-choco--4', '-50rem', '-30rem', 17)
      PlataArsinsPage.helper.chocoParallax('.pa-hero .pa-choco--5', '-120rem', '-50rem', -17)
      PlataArsinsPage.helper.cloudParallax('.pa-hero .pa-cloud--1', '-30rem', '-15rem')
      PlataArsinsPage.helper.cloudParallax('.pa-hero .pa-cloud--2', '40rem', '20rem')
      PlataArsinsPage.helper.cloudParallax('.pa-hero .pa-cloud--3', '-120rem', '-30rem')
      PlataArsinsPage.helper.cloudParallax('.pa-hero .pa-cloud--4', '50rem', '25rem')
    },

    info(): void {
      PlataArsinsPage.helper.chocoParallax('.pa-info .pa-choco--1', '-90rem', '-50rem', -12)
      PlataArsinsPage.helper.chocoParallax('.pa-info .pa-choco--2', '-180rem', '-80rem', 17)
      PlataArsinsPage.helper.chocoParallax('.pa-info .pa-choco--3', '-120rem', '-20rem', -33)
      PlataArsinsPage.helper.cloudParallax('.pa-info .pa-cloud--1', '30rem', '45rem')
      PlataArsinsPage.helper.cloudParallax('.pa-info .pa-cloud--2', '-120rem', '-90rem')

      ScrollTrigger.create({
        animation: gsap.to('.pa-info__card-background div', { ease: 'none', right: '0%' }),
        end: 'clamp(100% 0%)',
        scrub: true,
        start: 'clamp(0% 100%)',
        trigger: '.pa-info__card',
      })
    },

    cards(): void {
      PlataArsinsPage.helper.cloudParallax('.pa-cards .pa-cloud--1', '-90rem', '120rem')
      PlataArsinsPage.helper.cloudParallax('.pa-cards .pa-cloud--2', '120rem', '-120rem')

      const cardsContainer = document.querySelector<HTMLFormElement>('.pa-cards__container')
      const cardButtons = document.querySelectorAll<HTMLElement>('.pa-cards .pa-button')
      const voteInput = cardsContainer?.querySelector<HTMLInputElement>('input[name="Vote"]')
      const votedText = cardButtons[0].firstElementChild.getAttribute('data-voted') ?? 'Takk fyrir atkvæðin'

      const resetVote = (): void => {
        setTimeout(() => {
          cardButtons.forEach((button) => {
            button.classList.remove('pa-button--loading')
            button.removeAttribute('disabled')
          })
        }, 567)
      }

      const selectVote = async (index: number): Promise<void> => {
        if (localStorage.getItem('plataArsinsVoted')) return
        if (!cardsContainer || !voteInput) return

        cardButtons.forEach((button) => button.setAttribute('disabled', ''))
        cardButtons[index].classList.add('pa-button--loading')
        voteInput.value = (index + 1).toString()

        cardsContainer?.requestSubmit()
      }

      const markVote = (index: number) => {
        cardButtons[index].classList.remove('pa-button--loading')
        cardButtons[index].classList.add('pa-button--success')
        cardButtons[index].firstElementChild.textContent = votedText
        localStorage.setItem('plataArsinsVoted', (index + 1).toString())
        gsap.fromTo('.pa-card:nth-child(' + (index + 1) + ') .pa-card__badge', { rotation: -90, scale: 0 }, { ease: 'back.out', rotation: 0, scale: 1 })
        gsap.to('.pa-card:not(:nth-child(' + (index + 1) + '))', { ease: 'power3.out', filter: 'blur(4rem)', opacity: 0.8 })
      }

      cardButtons.forEach((button, index) => {
        button.addEventListener('click', () => {
          selectVote(index)
        })
      })

      cardsContainer?.addEventListener('submit', async (event) => {
        event.preventDefault()

        if (!cardsContainer || !voteInput?.value) return

        const index = parseInt(voteInput.value) - 1
        const action = cardsContainer.getAttribute('action')
        const method = cardsContainer.getAttribute('method') ?? 'POST'

        if (!action) {
          resetVote()
          return
        }

        try {
          const response = await fetch(action, {
            body: new FormData(cardsContainer),
            method,
          })

          const result = await response.json().catch(() => null)

          if (!response.ok) {
            resetVote()
            console.error(result?.error ?? `Vote failed: ${response.status}`)
            return
          }

          if (result?.success) {
            markVote(index)
            return
          }

          resetVote()
          console.error(result?.error ?? 'Vote failed')
        } catch (error) {
          resetVote()
          console.error(error)
        }
      })

      if (localStorage.getItem('plataArsinsVoted')) {
        const votedIndex = parseInt(localStorage.getItem('plataArsinsVoted'))
        cardButtons.forEach((button) => button.setAttribute('disabled', ''))
        cardButtons[votedIndex - 1].classList.add('pa-button--success')
        cardButtons[votedIndex - 1].firstElementChild.textContent = votedText
        gsap.set('.pa-card:nth-child(' + votedIndex + ') .pa-card__badge', { scale: 1 })
        gsap.set('.pa-card:not(:nth-child(' + votedIndex + '))', { filter: 'blur(4rem)', opacity: 0.8 })
      }
    },

    footer(): void {
      PlataArsinsPage.helper.chocoParallax('.pa-footer .pa-choco--1', '-90rem', '-70rem', -21)
      PlataArsinsPage.helper.chocoParallax('.pa-footer .pa-choco--2', '-40rem', '-40rem', 17)
      PlataArsinsPage.helper.chocoParallax('.pa-footer .pa-choco--3', '-120rem', '-70rem', 17)
      PlataArsinsPage.helper.chocoParallax('.pa-footer .pa-choco--4', '-30rem', '-120rem', -7)
      PlataArsinsPage.helper.cloudParallax('.pa-footer .pa-cloud--1', '-40rem', '-40rem')
      PlataArsinsPage.helper.cloudParallax('.pa-footer .pa-cloud--2', '-80rem', '30rem')
      PlataArsinsPage.helper.cloudParallax('.pa-footer .pa-cloud--3', '-20rem', '30rem')
      PlataArsinsPage.helper.cloudParallax('.pa-footer .pa-cloud--4', '40rem', '120rem')
      PlataArsinsPage.helper.cloudParallax('.pa-footer .pa-cloud--5', '-20rem', '-20rem')
      PlataArsinsPage.helper.cloudParallax('.pa-footer .pa-cloud--6', '-80rem', '-40rem')
      PlataArsinsPage.helper.cloudParallax('.pa-footer .pa-cloud--7', '-30rem', '-30rem')

      ScrollTrigger.create({
        animation: gsap
          //
          .timeline({ defaults: { ease: 'none' } })
          .fromTo('.pa-footer__person--hjorvar picture', { x: '15%', y: '35%' }, { x: '0%', y: '0%' }, 0)
          .fromTo('.pa-footer__person--herra picture', { x: '0%', y: '21%' }, { x: '0%', y: '0%' }, 0)
          .fromTo('.pa-footer__person--solrun picture', { x: '5%', y: '35%' }, { x: '0%', y: '0%' }, 0),
        end: '100% 100%',
        scrub: true,
        start: '0% 100%',
        trigger: '.pa-footer',
      })
    },
  },
}

export default PlataArsinsPage
